import { useRef, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { runLivenessChallenge, pickInstruction } from "../lib/liveness";
import { Button } from "./ui/button";
import { Camera, X, Loader2, ShieldCheck } from "lucide-react";

// Re-selfie chauffeur (contrôle périodique d'identité) — capture live + anti-fraude, upload sur /auth/profile/photo.
export function SelfieRecheck({ onDone, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [instruction, setInstruction] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const stop = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCameraOn(false);
  }, []);
  useEffect(() => () => stop(), [stop]);

  const start = async () => {
    setErr("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false });
      streamRef.current = s; setCameraOn(true); setInstruction(pickInstruction());
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); } }, 50);
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Accès caméra refusé." : "Caméra indisponible.");
    }
  };

  const sharpness = (canvas) => {
    const s = 128, tmp = document.createElement("canvas"); tmp.width = s; tmp.height = s;
    const c = tmp.getContext("2d"); c.drawImage(canvas, 0, 0, s, s);
    const d = c.getImageData(0, 0, s, s).data; const g = new Float32Array(s * s);
    for (let i = 0; i < s * s; i++) g[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
    let m = 0; const l = new Float32Array(s * s);
    for (let y = 1; y < s - 1; y++) for (let x = 1; x < s - 1; x++) { const i = y * s + x; l[i] = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - s] - g[i + s]; m += l[i]; }
    m /= s * s; let v = 0; for (let i = 0; i < s * s; i++) v += (l[i] - m) ** 2; return v / (s * s);
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return toast.error("Caméra pas encore prête.");
    const size = Math.min(video.videoWidth, video.videoHeight);
    const canvas = canvasRef.current; canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, (video.videoWidth - size) / 2, (video.videoHeight - size) / 2, size, size, 0, 0, size, size);
    if (sharpness(canvas) < 8) return toast.error("Photo floue, reprenez le selfie.");
    setChecking(true);
    const live = await runLivenessChallenge(video, instruction?.id);
    setChecking(false);
    if (!live.ok) return toast.error(live.reason);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stop(); setUploading(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData(); fd.append("file", blob, "selfie.jpg");
      await api.post("/auth/profile/photo", fd);
      toast.success("Selfie mis à jour ✓ Identité confirmée.");
      onDone && (await onDone());
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="selfie-recheck-modal" className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <canvas ref={canvasRef} className="hidden" />
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-head font-black text-lg text-[#1A1A1A]">Confirmez votre identité</h3>
          <button onClick={onClose} data-testid="recheck-close"><X className="w-5 h-5 text-[#75807A]" /></button>
        </div>
        <p className="mt-1 text-sm text-[#75807A]">Prenez un nouveau selfie en direct pour reprendre du service.</p>
        <div className="mt-4 flex flex-col items-center">
          <div className="relative w-40 h-40 rounded-full overflow-hidden border-2 border-[#E2DFD8] bg-[#0F0F0F] flex items-center justify-center">
            {cameraOn ? <video ref={videoRef} data-testid="recheck-video" autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" /> : <Camera className="w-9 h-9 text-white/50" />}
            {cameraOn && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className="w-28 h-32 rounded-[50%] border-2 border-dashed border-white/80" /></div>}
          </div>
          <div className="mt-4 w-full flex flex-col items-center gap-2">
            {cameraOn && instruction && (
              <div data-testid="recheck-instruction" className="w-full rounded-xl bg-[#1A1A1A] text-white text-sm font-bold px-4 py-2.5 text-center">
                {instruction.label}
              </div>
            )}
            {!cameraOn ? (
              <Button data-testid="recheck-open-camera" onClick={start} disabled={uploading} className="h-12 px-6 rounded-xl bg-[#1A1A1A] text-white font-bold inline-flex items-center gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Ouvrir la caméra
              </Button>
            ) : (
              <Button data-testid="recheck-capture" onClick={capture} disabled={checking} className="h-12 px-6 rounded-xl bg-[#D94F2B] text-white font-bold inline-flex items-center gap-2">{checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {checking ? "Vérification…" : "Prendre le selfie"}</Button>
            )}
          </div>
          {err && <p className="mt-2 text-xs text-[#D32F2F]">{err}</p>}
          <div className="mt-4 flex items-center gap-1.5 text-xs text-[#75807A]"><ShieldCheck className="w-4 h-4 text-[#D94F2B]" /> Sécurité anti-usurpation</div>
        </div>
      </div>
    </div>
  );
}

export default SelfieRecheck;
